import {
  Box3,
  BufferGeometry,
  Camera,
  Float32BufferAttribute,
  Frustum,
  Intersection,
  Material,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  Ray,
  Raycaster,
  Scene,
  Sphere,
  Uint32BufferAttribute,
  Vector2,
  Vector3,
  WebGLRenderer,
} from 'three';

import { isShownBelow } from '../../../utils/three/visibility';
import {
  Conic,
  getConicParameter,
  getConicPoint,
  tessellateConic,
} from '../design/design-node/utils/conic';
import {
  curveSteps,
  emptyStyledLinesData,
  floatsPerItem,
  readItem,
  straightKind,
  StyledItem,
  StyledLinesData,
  triangleKind,
} from './styled-lines-data';
import {
  createHiddenTexture,
  createItemTexture,
  createPatternTexture,
  createStyledLinesMaterial,
} from './styled-lines-material';
import { StripePattern } from './styled-segment';

/**
 * How far (pixels) the drawn steps of a curve may be from the curve.
 */
const tolerance = 0.5;

/**
 * Lines are at least this wide on screen (pixels).
 */
const minWidth = 1;

/**
 * The number of straight steps used when intersecting curves, before the point is moved onto the curve.
 */
const raycastCurveSteps = 16;

/**
 * Creates the vertices and triangles of the items. Each vertex has the index of its item and its
 * corner: 3 corners for a triangle, and pairs of corners on either side of a segment at each step
 * along it, 1 step for a straight segment and curveSteps for a curve. The index has room for the
 * triangles of all steps, see writeIndex.
 */
function createGeometry(data: StyledLinesData) {
  let vertexCount = 0;
  let indexCount = 0;

  for (let i = 0; i < data.count; i++) {
    const kind = data.items[i * floatsPerItem + 3];
    const steps = kind > 0 ? curveSteps : 1;
    vertexCount += kind === triangleKind ? 3 : (steps + 1) * 2;
    indexCount += kind === triangleKind ? 3 : steps * 6;
  }

  const position = new Float32Array(vertexCount * 3);
  let vertex = 0;

  for (let i = 0; i < data.count; i++) {
    const kind = data.items[i * floatsPerItem + 3];
    const corners = kind === triangleKind ? 3 : kind > 0 ? (curveSteps + 1) * 2 : 4;

    for (let corner = 0; corner < corners; corner++) {
      position[vertex * 3] = i;
      position[vertex * 3 + 1] = corner;
      vertex++;
    }
  }

  const index = new Uint32Array(indexCount);
  writeIndex(index, data);

  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new Float32BufferAttribute(position, 3));
  geometry.setIndex(new Uint32BufferAttribute(index, 1));

  return geometry;
}

/**
 * Writes the triangles of the items to the index, with the number of steps of each curve (in the
 * order of the items), or curveSteps for all of them. A curve with fewer steps uses every second,
 * fourth etc. pair of its corners, the vertices of the others are not drawn. Returns the number of
 * indices written.
 */
function writeIndex(index: Uint32Array, data: StyledLinesData, steps?: Uint8Array): number {
  let vertex = 0;
  let next = 0;
  let curve = 0;

  for (let i = 0; i < data.count; i++) {
    const kind = data.items[i * floatsPerItem + 3];

    if (kind === triangleKind) {
      index[next++] = vertex;
      index[next++] = vertex + 1;
      index[next++] = vertex + 2;
      vertex += 3;
      continue;
    }

    const curved = kind > 0;
    const count = curved ? steps?.[curve++] ?? curveSteps : 1;
    // The number of corners from one step to the next.
    const stride = curved ? (curveSteps / count) * 2 : 2;

    for (let step = 0; step < count; step++) {
      const a = vertex + step * stride;
      const b = a + stride;
      index[next++] = a;
      index[next++] = a + 1;
      index[next++] = b;
      index[next++] = a + 1;
      index[next++] = b + 1;
      index[next++] = b;
    }

    vertex += curved ? (curveSteps + 1) * 2 : 4;
  }

  return next;
}

/**
 * The values of each curve (in the order of the items) that its number of steps is chosen from:
 * the middle of its chord (x, y, z), the sagitta (from there to the middle of the curve), and the
 * radius around the middle of the chord that holds the curve and its width.
 */
const floatsPerCurveSize = 5;

/**
 * Gets the sizes of the curves of the items, see floatsPerCurveSize.
 */
function getCurveSizes(data: StyledLinesData): Float32Array {
  const items = data.items;
  const sizes: number[] = [];

  for (let i = 0; i < data.count; i++) {
    const offset = i * floatsPerItem;
    const weight = items[offset + 3];

    if (weight <= 0) {
      continue;
    }

    const middle = [0, 1, 2].map((axis) => (items[offset + axis] + items[offset + 4 + axis]) / 2);
    // The point of the curve at t = 0.5, as in conicPoint.
    const curveMiddle = [0, 1, 2].map(
      (axis) =>
        (items[offset + axis] + 2 * weight * items[offset + 8 + axis] + items[offset + 4 + axis]) /
        (2 + 2 * weight),
    );
    const distance = (point: number) =>
      Math.hypot(...[0, 1, 2].map((axis) => items[offset + point + axis] - middle[axis]));

    // A curve with a positive weight is within the triangle of its start, control point and end.
    sizes.push(
      ...middle,
      Math.hypot(...[0, 1, 2].map((axis) => curveMiddle[axis] - middle[axis])),
      Math.max(distance(0), distance(8)) + items[offset + 7] / 2,
    );
  }

  return new Float32Array(sizes);
}

const toConic = (item: StyledItem): Conic => ({
  start: item.start,
  control: item.control,
  end: item.end,
  weight: item.weight,
});

/**
 * Gets the bounds of all items, from the start, end and control point, which contain a curve, and
 * the width. Reads the floats directly, since there can be millions of items.
 */
function getDataBounds(data: StyledLinesData): Box3 {
  const min = [Infinity, Infinity, Infinity];
  const max = [-Infinity, -Infinity, -Infinity];
  const items = data.items;

  for (let i = 0; i < data.count; i++) {
    const offset = i * floatsPerItem;
    const halfWidth = items[offset + 7] / 2;

    for (const point of [0, 4, 8]) {
      for (let axis = 0; axis < 3; axis++) {
        const value = items[offset + point + axis];
        min[axis] = Math.min(min[axis], value - halfWidth);
        max[axis] = Math.max(max[axis], value + halfWidth);
      }
    }
  }

  return data.count > 0 ? new Box3(new Vector3(...min), new Vector3(...max)) : new Box3();
}

/**
 * Gets the distances along the ray where it enters and leaves the box, or undefined if it misses it.
 */
function getRayBoxRange(ray: Ray, box: Box3): [number, number] | undefined {
  let near = 0;
  let far = Infinity;

  for (const axis of ['x', 'y', 'z'] as const) {
    const origin = ray.origin[axis];
    const direction = ray.direction[axis];

    if (Math.abs(direction) < 1e-12) {
      if (origin < box.min[axis] || origin > box.max[axis]) {
        return undefined;
      }

      continue;
    }

    const a = (box.min[axis] - origin) / direction;
    const b = (box.max[axis] - origin) / direction;
    near = Math.max(near, Math.min(a, b));
    far = Math.min(far, Math.max(a, b));
  }

  return near <= far ? [near, far] : undefined;
}

/**
 * Below this number of items all of them are tested, which is faster than building a grid.
 */
const minGridItems = 2000;

const _center = new Vector3();
const _box = new Box3();
const _enter = new Vector3();
const _exit = new Vector3();

/**
 * A grid over the items in the horizontal plane, to find the items near a ray without testing all
 * of them, since drawings can have millions of items. A straight segment is in the cells it
 * crosses, a curve or triangle in the cells of the box around its points.
 *
 * It is in typed arrays instead of objects per item, so that it is fast to build and leaves no
 * garbage to collect.
 */
class ItemGrid {
  /** The center (x, y, z) and radius of a sphere around each item. */
  private readonly spheres: Float64Array;
  private readonly bounds = new Box3();
  private readonly cellSize: number = 1;
  private readonly columns: number = 1;
  private readonly rows: number = 1;
  /**
   * The items of cell c are cellItems[cellStart[c]] up to cellItems[cellStart[c + 1]].
   * Undefined when there are too few items for a grid.
   */
  private readonly cellStart?: Uint32Array;
  private readonly cellItems?: Uint32Array;
  /** The items found by the last query, and the query that each item was last found by. */
  private readonly found: number[] = [];
  private readonly foundBy: Uint32Array;
  private query = 0;

  constructor(private readonly data: StyledLinesData) {
    const count = data.count;
    this.spheres = new Float64Array(count * 4);
    this.foundBy = new Uint32Array(count);

    const min = [Infinity, Infinity, Infinity];
    const max = [-Infinity, -Infinity, -Infinity];

    for (let i = 0; i < count; i++) {
      this.setSphere(i);

      for (let axis = 0; axis < 3; axis++) {
        const center = this.spheres[i * 4 + axis];
        const radius = this.spheres[i * 4 + 3];
        min[axis] = Math.min(min[axis], center - radius);
        max[axis] = Math.max(max[axis], center + radius);
      }
    }

    this.bounds.set(new Vector3(...min), new Vector3(...max));

    if (count < minGridItems) {
      return;
    }

    const size = this.bounds.getSize(new Vector3());
    // A few items per cell, if they were spread out evenly.
    this.cellSize = Math.max(size.x, size.y, 1e-9) / Math.max(1, Math.sqrt(count / 4));
    this.columns = Math.max(1, Math.ceil(size.x / this.cellSize));
    this.rows = Math.max(1, Math.ceil(size.y / this.cellSize));

    // Counts the items of each cell, and then places them after the items of the cells before it.
    const cellCount = this.columns * this.rows;
    const cellStart = new Uint32Array(cellCount + 1);

    const countItem = (cell: number) => cellStart[cell + 1]++;

    for (let i = 0; i < count; i++) {
      this.visitItemCells(i, countItem);
    }

    for (let cell = 0; cell < cellCount; cell++) {
      cellStart[cell + 1] += cellStart[cell];
    }

    const cellItems = new Uint32Array(cellStart[cellCount]);
    const next = cellStart.slice(0, cellCount);
    const placeItem = (cell: number, item: number) => (cellItems[next[cell]++] = item);

    for (let i = 0; i < count; i++) {
      this.visitItemCells(i, placeItem);
    }

    this.cellStart = cellStart;
    this.cellItems = cellItems;
  }

  /**
   * Gets the items in the cells along the part of the ray that is within the bounds, each once.
   * The array is reused by the next query.
   */
  findNear(ray: Ray, reach: number): readonly number[] {
    const found = this.found;
    found.length = 0;

    const { cellStart, cellItems } = this;

    if (!cellStart || !cellItems) {
      for (let i = 0; i < this.data.count; i++) {
        found.push(i);
      }

      return found;
    }

    const range = getRayBoxRange(ray, _box.copy(this.bounds).expandByScalar(reach));

    if (!range) {
      return found;
    }

    if (this.query === 0xffffffff) {
      this.foundBy.fill(0);
      this.query = 0;
    }

    const query = ++this.query;
    const foundBy = this.foundBy;
    ray.at(range[0], _enter);
    ray.at(range[1], _exit);

    this.visitSegmentCells(_enter.x, _enter.y, _exit.x, _exit.y, reach, (cell) => {
      for (let k = cellStart[cell]; k < cellStart[cell + 1]; k++) {
        const i = cellItems[k];

        if (foundBy[i] !== query) {
          foundBy[i] = query;
          found.push(i);
        }
      }
    });

    return found;
  }

  /**
   * Whether the ray is within the distance of the sphere around the item.
   */
  isNear(item: number, ray: Ray, distance: number) {
    const offset = item * 4;
    const radius = this.spheres[offset + 3] + distance;
    _center.fromArray(this.spheres, offset);

    return ray.distanceSqToPoint(_center) <= radius * radius;
  }

  /**
   * Sets the sphere around the item, in the units of the positions.
   */
  private setSphere(item: number) {
    const items = this.data.items;
    const offset = item * floatsPerItem;
    const kind = items[offset + 3];
    const triangle = kind === triangleKind;

    // The middle of the start and end, or of the corners of a triangle.
    for (let axis = 0; axis < 3; axis++) {
      const a = items[offset + axis];
      const b = items[offset + 4 + axis];
      const c = items[offset + 8 + axis];
      this.spheres[item * 4 + axis] = triangle ? (a + b + c) / 3 : (a + b) / 2;
    }

    const toStart = this.distanceToCenter(item, 0);
    const toControl = this.distanceToCenter(item, 8);

    this.spheres[item * 4 + 3] = triangle
      ? Math.max(toStart, this.distanceToCenter(item, 4), toControl)
      : // A curve is within the triangle of its start, end and control point.
        (kind > 0 ? Math.max(toStart, toControl) : toStart) + items[offset + 7] / 2;
  }

  /**
   * The distance from a point of the item (0 start, 4 end, 8 control point) to its sphere center.
   */
  private distanceToCenter(item: number, point: number) {
    const offset = item * floatsPerItem + point;
    const dx = this.data.items[offset] - this.spheres[item * 4];
    const dy = this.data.items[offset + 1] - this.spheres[item * 4 + 1];
    const dz = this.data.items[offset + 2] - this.spheres[item * 4 + 2];

    return Math.sqrt(dx * dx + dy * dy + dz * dz);
  }

  /**
   * Visits the cells of the item, with the item.
   */
  private visitItemCells(item: number, visit: (cell: number, item: number) => void) {
    const items = this.data.items;
    const offset = item * floatsPerItem;
    const kind = items[offset + 3];
    const halfWidth = kind === triangleKind ? 0 : items[offset + 7] / 2;
    const ax = items[offset];
    const ay = items[offset + 1];
    const bx = items[offset + 4];
    const by = items[offset + 5];

    if (kind === straightKind) {
      this.visitSegmentCells(ax, ay, bx, by, halfWidth, visit, item);
      return;
    }

    const cx = items[offset + 8];
    const cy = items[offset + 9];

    this.visitBoxCells(
      Math.min(ax, bx, cx) - halfWidth,
      Math.min(ay, by, cy) - halfWidth,
      Math.max(ax, bx, cx) + halfWidth,
      Math.max(ay, by, cy) + halfWidth,
      visit,
      item,
    );
  }

  /**
   * Visits the cells that are within the distance of the segment from a to b, and possibly a few
   * more: in each row, the cells of the box around the part of the segment in the row.
   */
  private visitSegmentCells(
    ax: number,
    ay: number,
    bx: number,
    by: number,
    distance: number,
    visit: (cell: number, item: number) => void,
    item = -1,
  ) {
    // A little more, so that a segment along the edge of a cell is in the cells on both sides.
    const margin = distance + this.cellSize * 1e-6;
    const dx = bx - ax;
    const dy = by - ay;
    const firstRow = this.rowOf(Math.min(ay, by) - margin);
    const lastRow = this.rowOf(Math.max(ay, by) + margin);

    for (let row = firstRow; row <= lastRow; row++) {
      let t0 = 0;
      let t1 = 1;

      if (Math.abs(dy) > 1e-12) {
        const bottom = this.bounds.min.y + row * this.cellSize - margin;
        const top = bottom + this.cellSize + 2 * margin;
        const tBottom = (bottom - ay) / dy;
        const tTop = (top - ay) / dy;
        t0 = Math.max(0, Math.min(tBottom, tTop));
        t1 = Math.min(1, Math.max(tBottom, tTop));

        if (t0 > t1) {
          continue;
        }
      }

      const x0 = ax + dx * t0;
      const x1 = ax + dx * t1;
      const firstColumn = this.columnOf(Math.min(x0, x1) - margin);
      const lastColumn = this.columnOf(Math.max(x0, x1) + margin);

      for (let column = firstColumn; column <= lastColumn; column++) {
        visit(row * this.columns + column, item);
      }
    }
  }

  private visitBoxCells(
    x0: number,
    y0: number,
    x1: number,
    y1: number,
    visit: (cell: number, item: number) => void,
    item: number,
  ) {
    const lastRow = this.rowOf(y1);
    const lastColumn = this.columnOf(x1);

    for (let row = this.rowOf(y0); row <= lastRow; row++) {
      for (let column = this.columnOf(x0); column <= lastColumn; column++) {
        visit(row * this.columns + column, item);
      }
    }
  }

  private columnOf(x: number) {
    const column = Math.floor((x - this.bounds.min.x) / this.cellSize);
    return Math.min(this.columns - 1, Math.max(0, column));
  }

  private rowOf(y: number) {
    const row = Math.floor((y - this.bounds.min.y) / this.cellSize);
    return Math.min(this.rows - 1, Math.max(0, row));
  }
}

/**
 * Draws styled lines (straight segments, curves and circles, with widths and stripes) and
 * triangles, in one draw call and in the order of the items, so that a flat drawing keeps the
 * order it is drawn in.
 *
 * The items are in a texture that the shader reads (vertex pulling), and each vertex only has the
 * index of its item and its corner, so straight segments and triangles need few vertices.
 * The number of steps along a curve is chosen before each render from how large it is on screen,
 * and only those steps are in the triangles, see updateCurveSteps.
 *
 * Intersections are with the items within their width, or the line threshold of the raycaster for
 * thin lines, with the id of the item as index.
 *
 * The items of a layer are hidden when its object is, see layerObjects.
 */
export class StyledLines extends Mesh<BufferGeometry, MeshBasicMaterial> {
  private readonly uniforms = {
    lineItems: { value: createItemTexture(emptyStyledLinesData()) },
    lineResolution: { value: new Vector2(1, 1) },
    lineMinWidth: { value: minWidth },
    linePatterns: { value: createPatternTexture([]) },
    lineAnyHidden: { value: false },
    lineHidden: { value: createHiddenTexture(new Uint8Array()) },
  };

  private _data = emptyStyledLinesData();
  private grid?: ItemGrid;
  /** Whether each layer was hidden when the hidden items were last updated. */
  private hiddenLayers: boolean[] = [];
  /** Whether each item is hidden (1), undefined when none is. */
  private hiddenItems?: Uint8Array;
  /** The sizes of the curves, see getCurveSizes. */
  private curveSizes = new Float32Array();
  /** The number of steps each curve is drawn with, see updateCurveSteps. */
  private curveStepCounts = new Uint8Array();
  /** The view that the steps of the curves were chosen for: the matrix to clip space, and the resolution. */
  private readonly stepsView = { matrix: new Matrix4(), resolution: new Vector2() };

  /**
   * The object of each layer of the items (see StyledLinesData), e.g. the layer nodes of a PDF
   * page. The items of a layer are hidden when its object, or one of its ancestors below the
   * parent of these lines, is hidden.
   */
  layerObjects: Object3D[] = [];

  /**
   * A material to copy the settings from before rendering, e.g. the material of other lines that
   * these lines should look like.
   */
  baseMaterial?: Material;

  constructor(data?: StyledLinesData) {
    super(new BufferGeometry());
    this.material = createStyledLinesMaterial(this.uniforms);
    // Freed with the material, also when it is disposed by disposeGeometriesAndMaterials.
    this.material.addEventListener('dispose', () => {
      this.uniforms.lineItems.value.dispose();
      this.uniforms.linePatterns.value.dispose();
      this.uniforms.lineHidden.value.dispose();
    });
    this.setData(data ?? emptyStyledLinesData());
  }

  get data(): StyledLinesData {
    return this._data;
  }

  get patterns(): readonly StripePattern[] {
    return this._data.patterns;
  }

  /**
   * The items that are drawn, without the joins and the items of hidden layers.
   */
  getItems(): StyledItem[] {
    this.updateHiddenItems();

    return Array.from({ length: this._data.count }, (_, i) => i)
      .filter((i) => !this.hiddenItems?.[i])
      .map((i) => readItem(this._data, i))
      .filter((x) => x.id !== -1);
  }

  /**
   * Replaces the items.
   */
  setData(data: StyledLinesData) {
    this._data = data;
    this.grid = undefined;
    this.hiddenLayers = [];
    this.hiddenItems = undefined;
    this.uniforms.lineAnyHidden.value = false;

    this.geometry.dispose();
    this.geometry = createGeometry(data);
    this.curveSizes = getCurveSizes(data);
    this.curveStepCounts = new Uint8Array(this.curveSizes.length / floatsPerCurveSize);
    this.curveStepCounts.fill(curveSteps);
    // Chosen again before the next render.
    this.stepsView.resolution.set(0, 0);

    // The bounds can not be computed from the positions, since the vertices are placed in the shader.
    const bounds = getDataBounds(data);
    this.geometry.boundingBox = bounds;
    this.geometry.boundingSphere = bounds.isEmpty()
      ? new Sphere()
      : bounds.getBoundingSphere(new Sphere());

    this.uniforms.lineItems.value.dispose();
    this.uniforms.lineItems.value = createItemTexture(data);
    this.uniforms.linePatterns.value.dispose();
    this.uniforms.linePatterns.value = createPatternTexture(data.patterns);

    this.visible = data.count > 0;
  }

  override onBeforeRender(renderer: WebGLRenderer, _scene: Scene, camera: Camera) {
    renderer.getDrawingBufferSize(this.uniforms.lineResolution.value);
    this.updateCurveSteps(camera, this.uniforms.lineResolution.value);
  }

  /**
   * Chooses the number of steps of each curve for the view, so that they are within the tolerance
   * from the curve on screen, and draws only those steps. A curve outside of the view is drawn with
   * 1 step. The steps are a power of two, so that the triangles only change when a curve gets twice
   * as large or small on screen.
   */
  private updateCurveSteps(camera: Camera, resolution: Vector2) {
    const count = this.curveStepCounts.length;

    if (count === 0) {
      return;
    }

    // From the units of the positions to clip space.
    const matrix = new Matrix4()
      .multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse)
      .multiply(this.matrixWorld);
    const view = this.stepsView;

    if (view.resolution.equals(resolution) && view.matrix.equals(matrix)) {
      return;
    }

    view.matrix.copy(matrix);
    view.resolution.copy(resolution);

    // In the units of the positions, since the matrix is from them.
    const frustum = new Frustum().setFromProjectionMatrix(matrix);
    const sphere = new Sphere();
    const e = matrix.elements;
    // A length at the distance w (clip space) is this many pixels, divided by w, as in lineWorldPerPixel.
    const modelScale = new Vector3().setFromMatrixColumn(this.matrixWorld, 0).length();
    const pixelsPerUnit = (camera.projectionMatrix.elements[5] * resolution.y * modelScale) / 2;
    const sizes = this.curveSizes;
    let changed = false;

    for (let curve = 0; curve < count; curve++) {
      const offset = curve * floatsPerCurveSize;
      const [x, y, z] = [sizes[offset], sizes[offset + 1], sizes[offset + 2]];
      sphere.center.set(x, y, z);
      sphere.radius = sizes[offset + 4];
      const w = e[3] * x + e[7] * y + e[11] * z + e[15];
      let steps = curveSteps;

      if (!frustum.intersectsSphere(sphere)) {
        steps = 1;
      } else if (w > 0) {
        // The distance of n steps from the curve is about sagitta / n².
        const sagitta = (sizes[offset + 3] * pixelsPerUnit) / w;
        const needed = Math.ceil(Math.sqrt((2 * sagitta) / tolerance));
        steps = 1;

        while (steps < needed && steps < curveSteps) {
          steps *= 2;
        }
      }

      if (steps !== this.curveStepCounts[curve]) {
        this.curveStepCounts[curve] = steps;
        changed = true;
      }
    }

    if (!changed) {
      return;
    }

    const index = this.geometry.index!;
    const indexCount = writeIndex(index.array as Uint32Array, this._data, this.curveStepCounts);
    index.clearUpdateRanges();
    index.addUpdateRange(0, indexCount);
    index.needsUpdate = true;
    this.geometry.setDrawRange(0, indexCount);
  }

  /**
   * Copies the settings of the base material, and hides the items of hidden layers, before the
   * scene is rendered.
   */
  override updateMatrixWorld(force?: boolean) {
    super.updateMatrixWorld(force);
    this.updateHiddenItems();

    const base = this.baseMaterial as MeshBasicMaterial | undefined;

    if (!base) {
      return;
    }

    const material = this.material;

    if (material.transparent !== base.transparent) {
      material.needsUpdate = true;
    }

    material.color.copy(base.color);
    material.opacity = base.opacity;
    material.transparent = base.transparent;
    material.depthTest = base.depthTest;
    material.depthWrite = base.depthWrite;
    material.visible = base.visible;
  }

  override raycast(raycaster: Raycaster, intersects: Intersection[]) {
    const boundingSphere = this.geometry.boundingSphere;

    if (this._data.count === 0 || !boundingSphere) {
      return;
    }

    const threshold = raycaster.params.Line?.threshold ?? 1;
    const sphere = boundingSphere.clone().applyMatrix4(this.matrixWorld);
    sphere.radius += threshold;

    if (!raycaster.ray.intersectsSphere(sphere)) {
      return;
    }

    const inverseMatrix = new Matrix4().copy(this.matrixWorld).invert();
    const localRay = new Ray().copy(raycaster.ray).applyMatrix4(inverseMatrix);
    const scale = new Vector3().setFromMatrixScale(this.matrixWorld);
    const localThreshold = threshold / ((scale.x + scale.y + scale.z) / 3);

    this.grid ??= new ItemGrid(this._data);
    this.updateHiddenItems();
    const hiddenItems = this.hiddenItems;
    const pointOnRay = new Vector3();
    const pointOnSegment = new Vector3();

    for (const i of this.grid.findNear(localRay, localThreshold)) {
      if (hiddenItems?.[i] || !this.grid.isNear(i, localRay, localThreshold)) {
        continue;
      }

      const item = readItem(this._data, i);

      // Joins only fill the gaps between segments, the segments are intersected instead.
      if (item.id === -1) {
        continue;
      }

      let point: Vector3 | null = null;

      if (item.kind === 'triangle') {
        point = localRay.intersectTriangle(
          item.start,
          item.end,
          item.control,
          false,
          new Vector3(),
        );
      } else {
        const reach = localThreshold + item.width / 2;
        const points =
          item.kind === 'curve'
            ? tessellateConic(toConic(item), raycastCurveSteps)
            : [item.start, item.end];

        let closestDistanceSq = reach * reach;

        for (let j = 1; j < points.length; j++) {
          const distanceSq = localRay.distanceSqToSegment(
            points[j - 1],
            points[j],
            pointOnRay,
            pointOnSegment,
          );

          if (distanceSq <= closestDistanceSq) {
            closestDistanceSq = distanceSq;
            point = pointOnSegment.clone();
          }
        }

        // Move the point from the straight step onto the curve.
        if (point && item.kind === 'curve') {
          const conic = toConic(item);
          point = getConicPoint(conic, getConicParameter(conic, point));
        }
      }

      if (!point) {
        continue;
      }

      point.applyMatrix4(this.matrixWorld);
      const distance = raycaster.ray.origin.distanceTo(point);

      if (distance < raycaster.near || distance > raycaster.far) {
        continue;
      }

      const intersection: StyledLinesIntersection = {
        distance,
        point,
        index: item.id,
        face: null,
        faceIndex: undefined,
        object: this,
        item,
        itemIndex: i,
      };
      intersects.push(intersection);
    }
  }

  dispose() {
    this.geometry.dispose();
    this.material.dispose();
  }

  /**
   * Updates which items are hidden, if a layer has been hidden or shown since the last update.
   */
  private updateHiddenItems() {
    if (this.layerObjects.length === 0) {
      return;
    }

    const hiddenLayers = this.layerObjects.map((layer) => !isShownBelow(layer, this.parent));

    if (
      hiddenLayers.length === this.hiddenLayers.length &&
      hiddenLayers.every((hidden, i) => hidden === this.hiddenLayers[i])
    ) {
      return;
    }

    this.hiddenLayers = hiddenLayers;
    this.uniforms.lineHidden.value.dispose();

    if (!hiddenLayers.includes(true)) {
      this.hiddenItems = undefined;
      this.uniforms.lineAnyHidden.value = false;
      this.uniforms.lineHidden.value = createHiddenTexture(new Uint8Array());
      return;
    }

    const { layers, count } = this._data;
    const hiddenItems = new Uint8Array(count);

    for (let i = 0; i < count; i++) {
      hiddenItems[i] = hiddenLayers[layers[i]] ? 1 : 0;
    }

    this.hiddenItems = hiddenItems;
    this.uniforms.lineAnyHidden.value = true;
    this.uniforms.lineHidden.value = createHiddenTexture(hiddenItems);
  }
}

/**
 * An intersection with StyledLines, with the item that is intersected.
 */
export type StyledLinesIntersection = Intersection & {
  item: StyledItem;
  /** The index of the item in the data, see readItem. */
  itemIndex: number;
};

/**
 * Whether the object is StyledLines, so intersections with it are with an item.
 */
export const isStyledLines = (object?: Object3D): object is StyledLines =>
  object instanceof StyledLines;
